import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { WorkflowStepBatchConfigSchema } from "../../types/batch";
import {
  OPTIONAL_NUMERIC_TEXT_INPUT,
  OPTIONAL_TEXT_INPUT,
} from "./optionalInputs";

const text = OPTIONAL_TEXT_INPUT;
const numericText = OPTIONAL_NUMERIC_TEXT_INPUT;

export const purchaseOrderReleaseInputSchema = z.object({
  dataSourceLink: z.object({
    id: z.string().uuid(),
    dataSourceId: z.string().uuid().optional(),
  }),
  metadataId: z.string().min(1),
  changeOperation: z.enum(["insert", "update", "delete"]),
  changeLsn: z.string().regex(/^\d+$/),

  trackingNumber: numericText.describe("The release's tracking number."),
  receivingBranchCode: text.describe(
    "Receiving branch code, resolved to a warehouse origin id.",
  ),
  supplierCode: numericText.describe(
    "Vendor number, assigned per division. See supplierCodeStrategy.",
  ),

  shipViaCode: text,
  freightTermsCode: text,
  creditTermsCode: text,
  referenceNumber: text,
  fob: text,
  backorderFlag: text,
  substitutionFlag: text,
  shipCompleteFlag: text,
  ediFlag: text,
  faxFlag: text,
  urgentCode: text,

  headerComment1: text,
  headerComment2: text,
  headerComment3: text,
  headerComment4: text,
  headerComment5: text,
  headerComment6: text,
  headerComment7: text,
  headerComment8: text,
  headerComment9: text,
  headerComment10: text,

  specialInstruction1: text,
  specialInstructionCode1: text,
  specialInstruction2: text,
  specialInstructionCode2: text,
  specialInstruction3: text,
  specialInstructionCode3: text,

  emailTo: text,
  emailCc: text,
  emailBcc: text,
  emailFrom: text,
  subject: text,
  body1: text,
  body2: text,
  body3: text,
  body4: text,
  body5: text,
  body6: text,

  requiredDate: numericText.describe("Packed YYYYMMDD or ISO date."),
  releasedBy: text,
  releasedOn: numericText.describe("Packed YYYYMMDD or ISO date."),
  erpOrderNumber: numericText,
  erpStatus: text,
  erpError: text,
});
export type PurchaseOrderReleaseInput = z.infer<
  typeof purchaseOrderReleaseInputSchema
>;

export const upsertPurchaseOrderReleaseStep = defineStepConfig({
  type: "upsert_purchase_order_release",
  name: "Upsert Purchase Order Release",
  description:
    "Keeps the history of purchase-order releases sent to the ERP with their freight, terms, instructions and email choices, including CDC deletion and ordering.",
  category: "Transformation",
  libraryModule: "Procurement",
  inputSchema: purchaseOrderReleaseInputSchema,
  outputSchema: z.object({
    written: z.boolean(),
    deleted: z.boolean(),
    skipped: z.boolean(),
    reason: z
      .enum([
        "missing_tracking_number",
        "missing_division",
        "stale_event",
        "not_found",
      ])
      .nullable(),
  }),
  configSchema: z.object({
    batch: WorkflowStepBatchConfigSchema.prefault({ size: 500 }),
    writeOriginSystem: z.string().min(1),
    warehouseOriginSystem: z
      .string()
      .optional()
      .describe("Warehouse lookup origin system."),
    supplierOriginSystem: z
      .string()
      .optional()
      .describe("Supplier lookup origin system."),
    supplierCodeStrategy: z
      .enum(["as_is", "division_prefixed"])
      .prefault("division_prefixed")
      .describe(
        "`division_prefixed` tries `<DIVISION><code padded to 5>` before the bare code, for supplier catalogs keyed per division.",
      ),
  }),
  emits: [] as const,
  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
